import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import {styled} from '@mui/material/styles';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import CustomError from '../CustomError/CustomError';
import CustomToolTip from '../CustomToolTip/CustomToolTip';

const StyledButton = styled(Button)(({theme, highlight}) => ({
  overflow: 'hidden',
  '&.MuiButton-root': {
    fontWeight: 700,
    textTransform: 'none',
    '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
      boxShadow: theme.palette.boxShadow.button,
    },
    '&.custom-button-loading': {
      pointerEvents: 'none',
      opacity: '0.7',
    },
  },

  '&.color-secondary': {
    color: 'white',
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  ...(highlight === 'true'
    ? {
        '& span:nth-of-type(1)': {
          position: 'absolute',
          width: '100%',
          height: '5px',
          top: 0,
          left: 0,
          background: theme.palette.secondary.dark,
          animation: 'animate1 1s linear infinite',
        },
        '@keyframes animate1': {
          '0%': {
            transform: 'translateX(-100%)',
          },
          '100%': {
            transform: 'translateX(100%)',
          },
        },
        '& span:nth-of-type(2)': {
          position: 'absolute',
          width: '5px',
          height: '100%',
          top: 0,
          right: 0,
          background: theme.palette.secondary.dark,
          animation: 'animate2 1s linear infinite',
          animationDelay: '0.5s',
        },
        '@keyframes animate2': {
          '0%': {
            transform: 'translateY(-100%)',
          },
          '100%': {
            transform: 'translateY(100%)',
          },
        },
        '& span:nth-of-type(3)': {
          position: 'absolute',
          width: '100%',
          height: '5px',
          left: 0,
          bottom: 0,
          background: theme.palette.secondary.dark,
          animation: 'animate3 1s linear infinite',
        },
        '@keyframes animate3': {
          '0%': {
            transform: 'translateX(100%)',
          },
          '100%': {
            transform: 'translateX(-100%)',
          },
        },
        '& span:nth-of-type(4)': {
          position: 'absolute',
          width: '5px',
          height: '100%',
          top: 0,
          left: 0,
          background: theme.palette.secondary.dark,
          animation: 'animate4 1s linear infinite',
          animationDelay: '0.5s',
        },
        '@keyframes animate4': {
          '0%': {
            transform: 'translateY(100%)',
          },
          '100%': {
            transform: 'translateY(-100%)',
          },
        },
      }
    : {}),
}));

const StyledCustomToolTip = styled(CustomToolTip)(({theme}) => ({
  color: theme.palette.error.light,
  marginLeft: theme.spacing(0.5),
  fontSize: '1em',
}));

function CustomButton({
  text,
  onClick,
  disabled,
  isLoading,
  className = '',
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  type = 'button',
  error,
  startIcon,
  endIcon,
  to,
  target,
  dataAttributes,
  highlight,
  menuItems,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOnClick = (e) => {
    if (Array.isArray(menuItems) && menuItems.length) {
      setAnchorEl(e.currentTarget);
    } else {
      onClick();
    }
  };
  return (
    <>
      <StyledButton
        startIcon={startIcon && startIcon}
        endIcon={endIcon && endIcon}
        className={`${className} custom-button ${error ? 'custom-button-error' : ''} ${
          isLoading ? `custom-button-loading` : ''
        } `}
        onClick={!isLoading && handleOnClick ? handleOnClick : undefined}
        color={color}
        variant={variant}
        size={size}
        type={type}
        disabled={isLoading || disabled}
        to={to}
        component={to ? Link : 'button'}
        target={target}
        {...dataAttributes}
        highlight={`${highlight}`}
      >
        {isLoading ? (
          <CircularProgress color="inherit" size={25} disableShrink />
        ) : (
          <>
            {text}{' '}
            {error && (
              <StyledCustomToolTip title={<CustomError message={error} />} placement="top-end" arrow>
                <WarningIcon fontSize="small" />
              </StyledCustomToolTip>
            )}
            {highlight && (
              <>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </>
            )}
          </>
        )}
      </StyledButton>
      {Array.isArray(menuItems) && menuItems.length && (
        <Paper>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
          >
            {menuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  setAnchorEl(null);
                  item.onClick();
                }}
              >
                {item.text}
              </MenuItem>
            ))}
          </Menu>
        </Paper>
      )}
    </>
  );
}

export default CustomButton;
